import {Component, Input, OnInit, Optional} from '@angular/core';
import {Questionnaire} from '@ngmedax/common-questionnaire-types';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {QuestionnaireVariablesService} from '../../../services/questionnaire-variables.service';
import {QuestionnaireStateService} from '../../../services/questionnaire-state.service';
import {TRANSLATION_EDITOR_SCOPE} from '../../../../constants';
import {KEYS} from '../../../../translation-keys';

export interface MatrixCellSettingsComponent extends Translatable {}

/** Edits the variable mappings and scoring of one selected matrix cell. */
@Component({
  selector: 'app-qa-matrix-cell-settings',
  templateUrl: './matrix-cell-settings.component.html',
  styleUrls: ['./matrix-cell-settings.component.css']
})
@Translatable({scope: TRANSLATION_EDITOR_SCOPE, keys: KEYS})
export class MatrixCellSettingsComponent implements OnInit {
  @Input() container: Questionnaire.Container;
  public activeSection: 'scoring' | 'variables' = 'scoring';
  public variablesOpened = false;
  public selectedScore: string = null;
  public scoreValue: number = 0;
  public readonly disabledScopes = {inline: false, upload: true};
  private savedScore: string = null;
  private savedValue: number = 0;

  /** Injects the shared mapping services. */
  public constructor(
    @Optional() private translationService: TranslationService,
    private variables: QuestionnaireVariablesService,
    private state: QuestionnaireStateService
  ) {}

  /** Loads the saved mapping for this cell. */
  public ngOnInit() {
    this.resetScore();
  }

  /** Opens the questionnaire scoring tab without creating a variable. */
  public openScoringVariables(): void {
    this.state.onOpenScoringVariables.emit();
  }

  /** Returns available scoring variables, including the currently edited one. */
  public get scoringNames(): string[] {
    return this.variables.getVariableNamesForScope('scoring');
  }

  /** Allows saving valid, changed values, including zero and negative scores. */
  public canSaveScore(): boolean {
    return !!this.container.pathHash && !!this.selectedScore && this.scoringNames.indexOf(this.selectedScore) !== -1
      && typeof this.scoreValue === 'number' && Number.isFinite(this.scoreValue)
      && (this.selectedScore !== this.savedScore || this.scoreValue !== this.savedValue);
  }

  /** Persists the score using the existing questionnaire mapping format. */
  public saveScore() {
    if (!this.canSaveScore()) {
      return;
    }
    this.variables.addVariableMapping('scoring', this.selectedScore, this.container.pathHash, {value: this.scoreValue});
    this.resetScore();
    this.state.onUpdateElement.emit({element: null, position: null});
  }

  /** Restores the saved score without changing questionnaire data. */
  public resetScore() {
    this.savedScore = this.variables.getMappedVariableName('scoring', this.container.pathHash) || null;
    const options = this.savedScore
      ? this.variables.getMappedVariableOptions('scoring', this.container.pathHash, this.savedScore) : null;
    this.savedValue = options && typeof options.value === 'number' ? options.value : 0;
    this.selectedScore = this.savedScore;
    this.scoreValue = this.savedValue;
  }

  /** Checks whether this cell has a saved mapping to remove. */
  public hasSavedScore(): boolean {
    return !!this.savedScore;
  }

  /** Removes the persisted mapping rather than any unsaved selection. */
  public deleteScore() {
    if (!this.savedScore) {
      return;
    }
    this.variables.deleteVariableMapping('scoring', this.savedScore, this.container.pathHash);
    this.resetScore();
    this.state.onUpdateElement.emit({element: null, position: null});
  }

  /** Saves a score with Enter or the local save shortcut. */
  public onScoreKeyDown(event: KeyboardEvent) {
    const saveShortcut = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's';
    if (event.key !== 'Enter' && !saveShortcut) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.saveScore();
  }
}
